package pl.decks.personal.pages.ievp.sections.why

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.functions.min
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.fa.FaCircleChevronDown
import com.varabyte.kobweb.silk.components.icons.fa.FaPlay
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.deg
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.times
import org.jetbrains.compose.web.css.vh
import pl.decks.personal.components.widgets.LeadingIconRow
import pl.decks.personal.components.widgets.button.ButtonStylesheet
import pl.decks.personal.components.widgets.disclaimer.DisclaimerBox
import pl.decks.personal.components.widgets.hasdiagram.HASDiagramStylesheet
import pl.decks.personal.components.widgets.hasdiagram.HASNodePosition
import pl.decks.personal.components.widgets.hasdiagram.HubAndSpokeDiagram
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.pages.ievp.IEVPJourney
import pl.decks.personal.pages.ievp.IEVPSection
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout
import pl.decks.posthog.client.PostHogClient
import kotlin.math.pow

private enum class Rationale(
    val titleParts: List<String>,
    val explanation: String,
) {
    PEOPLE(
        listOf("People"),
        "The employee is never hired in vain. " +
                "They join a bigger picture. " +
                "The company is made of real people, not procedures and proclamations. " +
                "And who knows them better than the employer?"
    ),
    CULTURE(
        listOf("Culture"),
        "The people hired set up the working culture everyone follows. " +
                "Though goals are important, it is not about declarations and stride-fors. " +
                "Culture is predominantly about situations you might be ashamed to put in public writing. " +
                "How does the team operate, when everything breaks apart?",
    ),
    BUSINESS_MODEL(
        listOf("Business", "Model"),
        "How the business works and where it is heading determines who they look out for. " +
                "There are so many quirks and nuances... " +
                "The implicit details make it hard to verbalize why someone is not built for the job. " +
                "Or why they are right, even if they do not exactly fit the mold."
    ),
    PRODUCT(
        listOf("Product"),
        "A lot of how the company operates comes from what they are working on. " +
                "It influences the team operations, client outreach and development possibilities, to say the least. " +
                "On the outside the product looks polished, but there is always more to it. " +
                "It might be duct tape and prayers holding it together. " +
                "It might be ambitious plans, outside the official roadmap, spoken in whispers in fear of over-promise."
    ),
    CAPABILITIES(
        listOf("Capa-", "bilities"),
        "Every company lives in a strange state, never operating at the level it aims for. " +
                "There will always be processes in need of optimization. " +
                "Plans constrained by unspoken priorities, stretched attention, or team limitations. " +
                "Things quietly waiting for someone to own them. " +
                "It takes a lot of insight and information to identify the gaps and to decide " +
                "whether someone will elevate the company to a higher league, or suffocate its momentum."
    ),
    STRUGGLES(
        listOf("Struggles"),
        "It all converges into one fact: there is no one who has all of the challenges better mapped. " +
                "Who knows them to the bone and lives by them. " +
                "It is way easier to see the sprawling, interconnected company tissue from the inside."
    ),
    NEEDS(
        listOf("Needs"),
        "So, should it be the employee that looks at the EVP and decides \"I am what they need\"? " +
                "Possibly. " +
                "It does wonders for surface-level overlap: " +
                "Are we heading in the same direction? " +
                "Do we converge on values? " +
                "Can it be... exciting? " +
                "Yet, that is just one side of the story. " +
                "To truly meet the desired goals, to achieve mutual growth - " +
                "the communication should flow two-ways."
    ),
    ;

    @Composable
    fun nodeContent() = Column(horizontalAlignment = Alignment.CenterHorizontally) {
        titleParts.forEach {
            SpanText(it)
        }
    }

    fun fullTitle(): String = titleParts.joinToString(" ").replace("- ", "")
}


@Composable
fun IEVPWhySectionContent(sectionProgress: Float, onProceedClicked: () -> Unit) {
    val introSegments = 2
    val bufferSegments = 1.5f
    val revealSegments = Rationale.entries.size + 3 // outlines, lines, inner
    val totalSegments = introSegments + revealSegments + 2 * bufferSegments

    val segmentSize = 1f / totalSegments
    Column(
        modifier = WhySectionStylesheet.WhySection.toModifier()
            .minHeight(totalSegments * WhySectionStylesheet.Vars.segmentHeight.value())
    ) {
        IEVPWhyIntro(takenSegments = introSegments, onProceedClicked = onProceedClicked)
        IEVPRationaleDiagram(
            sectionProgress = sectionProgress,
            offset = introSegments + bufferSegments,
            revealSegments = revealSegments + 2 * bufferSegments,
            segmentSize = segmentSize,
        )

    }
}

@Composable
private fun IEVPWhyIntro(takenSegments: Int, onProceedClicked: () -> Unit) = Column(
    modifier = Modifier.fillMaxWidth().gap(SiteLayout.Space.Large).minHeight(takenSegments * WhySectionStylesheet.Vars.segmentHeight.value()),
    horizontalAlignment = Alignment.CenterHorizontally,
    verticalArrangement = Arrangement.Center,
) {
    DisclaimerBox(
        modifier = Modifier.fillMaxWidth().fontSize(FontTypography.Size.Small)
            .onClick { PostHogClient.capture(IEVPJourney.DisclaimerOpened(IEVPSection.WHY)) }
    ) {
        SpanText("Even though I did a lot to make it mobile-friendly, the site is still probably best experienced over larger screen. Also, safari breaks this section's diagram. I'll get to it eventually.")
    }
    IEVPIntroText()
    IEVPWhyIntroNextSectionPrompt(onProceedClicked)
    IEVPWhyIntroScrollFurtherPrompt()
}

@Composable
private fun IEVPIntroText() = Column(WhySectionStylesheet.Intro.Container.toModifier()) {
    SpanText(
        "EVP stands for Employee Value Proposition.",
        modifier = WhySectionStylesheet.Intro.Text.toModifier(),
    )
    SpanText(
        "These are benefits, rewards, the people, culture... " +
                "Everything that comprises the experience of working at a company. " +
                "What it offers, what it stands by. " +
                "The answer to why your company is THE company worth committing to. ",
        modifier = WhySectionStylesheet.Intro.Text.toModifier(),
    )
    SpanText(
        "However well you manage to communicate it, " +
                "the hiring process will always carry some information disparity, don't you think?",
        modifier = WhySectionStylesheet.Intro.Breakline.toModifier(),
    )
    SpanText(
        "So, let me fight it and coin Inverse EVP.",
        modifier = WhySectionStylesheet.Intro.Conclusion.toModifier(),
    )
    SpanText(
        "If you are already familiar with EVPs, it's self-explanatory...",
        modifier = WhySectionStylesheet.Intro.Text.toModifier(),
    )
}

@Composable
private fun IEVPWhyIntroNextSectionPrompt(onProceedClicked: () -> Unit) = IntroActionPrompt(
    text = "...and you can proceed to the next section",
    modifier = Modifier
        .setVariable(WhySectionStylesheet.Vars.emphasisColor, ColorMode.current.toSitePalette().accents.primary)
) {
    Button(
        onClick = { onProceedClicked() },
        variant = ButtonStylesheet.GhostVariant,
    ) {
        LeadingIconRow(
            icon = { FaPlay() },
            modifier = WhySectionStylesheet.Intro.ActionVisual.toModifier(),
        ) { SpanText("Elevator Pitch") }
    }
}

@Composable
private fun IEVPWhyIntroScrollFurtherPrompt() = IntroActionPrompt("... but you can scroll to see my rationale") {
    FaCircleChevronDown(modifier = WhySectionStylesheet.Intro.ActionVisual.toModifier())
}

@Composable
private fun IntroActionPrompt(
    text: String,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) = Column(
    modifier = Modifier.fillMaxWidth().then(modifier),
    horizontalAlignment = Alignment.CenterHorizontally,
    verticalArrangement = Arrangement.spacedBy(SiteLayout.Space.XXSmall)
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(text, modifier = WhySectionStylesheet.Intro.ActionPrompt.toModifier())
        HorizontalDivider(modifier = WhySectionStylesheet.Intro.Divider.toModifier())
    }
    content()
}

@Composable
private fun IEVPRationaleDiagram(
    sectionProgress: Float,
    offset: Float,
    revealSegments: Float,
    segmentSize: Float,
) = Box(Modifier.fillMaxWidth().minHeight(revealSegments * WhySectionStylesheet.Vars.segmentHeight.value())) {
    Column(
        modifier = WhySectionStylesheet.RevealingDiagram.StickyContainer.toModifier(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        IEVPDiagramUnfoldingDrawing(sectionProgress, offset, segmentSize)
        IEVPDiagramRollingDescription(sectionProgress, offset, segmentSize)
    }
}

@Composable
private fun IEVPDiagramUnfoldingDrawing(
    sectionProgress: Float,
    offset: Float,
    segmentSize: Float,
) {
    val outerNodeModifier = WhySectionStylesheet.RevealingDiagram.NodeText.toModifier()
    val innerNodeModifier = WhySectionStylesheet.RevealingDiagram.InnerNodeText.toModifier()
    HubAndSpokeDiagram(
        numberOuters = Rationale.entries.size,
        modifier = Modifier.width(min(100.percent, 60.vh)).aspectRatio(1),
        lineVariant = HASDiagramStylesheet.ConnectionLines.NoGuideVariant,
    ) {
        val outlineProgress = subsectionProgress(sectionProgress, segmentSize, atStep = Rationale.entries.size + offset)
        val lineProgress =
            subsectionProgress(sectionProgress, segmentSize, atStep = Rationale.entries.size + offset + 1)
        val innerProgress = subsectionProgress(
            sectionProgress + 0.5f * segmentSize,
            segmentSize,
            atStep = Rationale.entries.size + offset + 2,
        )

        Rationale.entries.forEach { entry ->
            val entryProgress = subsectionProgress(sectionProgress, segmentSize, atStep = entry.ordinal + offset)
            node(
                position = HASNodePosition.Outer(entry.ordinal),
                variant = HASDiagramStylesheet.Node.PartialArcVariant,
                modifier = outerNodeModifier
                    .opacity(entryProgress)
                    .setVariable(HASDiagramStylesheet.Vars.Internal.nodeSweepAngle, (outlineProgress * 360f).deg),
            ) { entry.nodeContent() }
        }

        line { fraction = lineProgress }

        node(
            position = HASNodePosition.Inner,
            modifier = innerNodeModifier
                .opacity(if (innerProgress > 0f) 1 else 0)
                .setVariable(
                    HASDiagramStylesheet.Vars.Internal.nodeContentSize,
                    innerProgress * HASDiagramStylesheet.Vars.innerNodeSize.value()
                ),
        ) {
            if (innerProgress > 0.5f) SpanText("IEVP")
        }
    }
}

@Composable
private fun IEVPDiagramRollingDescription(
    sectionProgress: Float,
    offset: Float,
    segmentSize: Float,
) = Box(Modifier.fillMaxWidth()) {
    val innerProgress = subsectionProgress(sectionProgress, segmentSize, atStep = Rationale.entries.size + offset + 2)

    Rationale.entries.forEach { entry ->
        val explanationProgress = subsectionProgress(
            sectionProgress - 0.5f * segmentSize,
            segmentSize,
            atStep = entry.ordinal + offset
        )
        IEVPDiagramPredicate(
            prefix = "Predicate ${entry.ordinal + 1}. ",
            title = entry.fullTitle(),
            explanation = entry.explanation,
            modifier = Modifier.fillMaxWidth()
                .opacity(if (explanationProgress > 0f && explanationProgress < 1f) 1f else 0f),
        )
    }

    IEVPDiagramPredicate(
        prefix = "Conclusion. ",
        title = "IEVP",
        explanation = "The employer has all the rich, complex, oftentimes unspeakable knowledge about the company. " +
                "Why should they compress it into a job posting? " +
                "Why would an employee hide behind a CV? " +
                "That is so last century. " +
                "The IEVP is an attempt to present myself as openly and thoroughly upfront. " +
                "My voice, no short bullet points - so you get a real sense of who you would be working with. " +
                "Time is the one thing neither of us would like to go to waste.",
        modifier = Modifier.fillMaxWidth().opacity(innerProgress.pow(0.5f)),
    )
}

@Composable
private fun IEVPDiagramPredicate(
    prefix: String,
    title: String,
    explanation: String,
    modifier: Modifier = Modifier
) = Column(
    modifier = modifier,
    horizontalAlignment = Alignment.CenterHorizontally,
    verticalArrangement = Arrangement.spacedBy(SiteLayout.Space.Small),
) {
    Row {
        SpanText(prefix, modifier = WhySectionStylesheet.RevealingDiagram.PredicatePrefix.toModifier())
        SpanText(title, modifier = WhySectionStylesheet.RevealingDiagram.PredicateTitle.toModifier())
    }
    SpanText(explanation, modifier = WhySectionStylesheet.RevealingDiagram.PredicateText.toModifier())
}

private fun subsectionProgress(
    sectionProgress: Float,
    stepSize: Float,
    atStep: Float,
): Float = ((sectionProgress - atStep * stepSize) / stepSize).coerceIn(0f, 1f)
